import { useEffect, useRef, useState } from "react";
import { ArrowRight, MessageCircle, Send, Sparkles, X, Bot, User as UserIcon } from "lucide-react";
import { publicApi } from "@/lib/api";

const SUGGESTIONS = [
  "What does the $1,000 plan include?",
  "How do you help with grants?",
  "Can you build my data room?",
  "Which countries do you work in?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [started, setStarted] = useState(false);
  const scrollRef = useRef(null);

  // Listen for "open chat" event dispatched from CTAs
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("seedtech:open-chat", handler);
    return () => window.removeEventListener("seedtech:open-chat", handler);
  }, []);

  useEffect(() => {
    const sid = localStorage.getItem("seedtech_session_id");
    if (sid) {
      publicApi
        .get(`/chat/messages/${sid}`)
        .then((r) => {
          setSession(r.data.session);
          setMessages(r.data.messages);
          setStarted(true);
        })
        .catch(() => localStorage.removeItem("seedtech_session_id"));
    }
  }, []);

  // Poll for new messages (catches admin human replies)
  useEffect(() => {
    if (!session?.id || !open) return;
    const tick = async () => {
      try {
        const r = await publicApi.get(`/chat/messages/${session.id}`);
        setMessages(r.data.messages);
      } catch (e) {
        /* noop */
      }
    };
    const iv = setInterval(tick, 4000);
    return () => clearInterval(iv);
  }, [session?.id, open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const startSession = async (e) => {
    e?.preventDefault();
    const { data } = await publicApi.post("/chat/start", { name: name || "Guest", email: email || null });
    localStorage.setItem("seedtech_session_id", data.session.id);
    setSession(data.session);
    setMessages(data.messages);
    setStarted(true);
  };

  const sendMessage = async (override) => {
    const t = (override ?? text).trim();
    if (!t || !session) return;
    setSending(true);
    setText("");
    // optimistic
    const tempId = `temp-${Date.now()}`;
    setMessages((m) => [...m, { id: tempId, sender: "visitor", text: t, created_at: new Date().toISOString() }]);
    try {
      const { data } = await publicApi.post("/chat/message", { session_id: session.id, text: t });
      setMessages((m) => {
        const filtered = m.filter((x) => x.id !== tempId);
        return [...filtered, data.user_message, ...(data.replies || [])];
      });
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== tempId));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="chat-toggle-button"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-24 right-6 z-[9999] inline-flex items-center gap-2 rounded-full bg-umber px-5 py-4 text-cream shadow-2xl shadow-umber/30 transition hover:-translate-y-0.5 hover:bg-umber-dark"
        aria-label="Open chat"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
        <span className="hidden sm:inline text-sm font-medium">{open ? "Close" : "Chat with us"}</span>
        {!open && <span className="ml-1 h-2 w-2 rounded-full bg-gold animate-pulse-dot" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-44 right-4 sm:right-6 z-[9998] flex h-[560px] w-[calc(100vw-2rem)] max-w-[380px] flex-col overflow-hidden rounded-2xl border border-border bg-white/95 shadow-2xl backdrop-blur-xl animate-fade-up"
        >
          {/* Header */}
          <div className="flex items-start justify-between bg-umber px-5 py-4 text-cream">
            <div>
              <div className="flex items-center gap-2 font-display text-lg font-semibold">
                SeedBot
                <span className="inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-gold">
                  <Sparkles className="h-3 w-3" /> AI + Human
                </span>
              </div>
              <div className="text-xs text-cream/70">Usually replies in minutes</div>
            </div>
            <button
              data-testid="chat-close-button"
              onClick={() => setOpen(false)}
              className="rounded-md p-1 text-cream/70 hover:bg-white/10"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          {!started ? (
            <form onSubmit={startSession} className="flex flex-1 flex-col gap-3 p-5">
              <p className="font-display text-lg text-umber">Tell us a little about yourself</p>
              <input
                data-testid="chat-input-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="rounded-md border border-border bg-white px-3 py-2 text-sm focus:border-umber focus:outline-none"
              />
              <input
                data-testid="chat-input-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email (optional)"
                type="email"
                className="rounded-md border border-border bg-white px-3 py-2 text-sm focus:border-umber focus:outline-none"
              />
              <button
                data-testid="chat-start-button"
                type="submit"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-md bg-terracotta px-4 py-2.5 text-sm font-medium text-cream transition hover:bg-terracotta-dark"
              >
                Start chat <ArrowRight className="h-4 w-4" />
              </button>
              <p className="mt-auto text-[11px] text-muted-foreground">
                By starting a chat you agree to our standard terms. We never share your info.
              </p>
            </form>
          ) : (
            <>
              <div ref={scrollRef} className="chat-scroll flex-1 space-y-3 overflow-y-auto bg-sand/40 px-4 py-4">
                {messages.map((m) => (
                  <MessageBubble key={m.id} msg={m} />
                ))}
                {sending && (
                  <div className="flex items-center gap-1 px-2 text-xs text-umber/60">
                    <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-umber" />
                    <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-umber" style={{ animationDelay: "0.2s" }} />
                    <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-umber" style={{ animationDelay: "0.4s" }} />
                    <span className="ml-2">SeedBot is typing…</span>
                  </div>
                )}
              </div>

              {messages.length <= 2 && (
                <div className="flex flex-wrap gap-1.5 border-t border-border bg-white px-3 py-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid={`chat-suggestion-${s.slice(0, 12).replace(/\s+/g, "-")}`}
                      onClick={() => sendMessage(s)}
                      className="rounded-full border border-border bg-sand px-3 py-1 text-[11px] text-umber hover:border-umber"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendMessage();
                }}
                className="flex items-center gap-2 border-t border-border bg-white p-3"
              >
                <input
                  data-testid="chat-message-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type your question…"
                  className="flex-1 rounded-md border border-border bg-white px-3 py-2 text-sm focus:border-umber focus:outline-none"
                />
                <button
                  data-testid="chat-send-button"
                  type="submit"
                  disabled={sending || !text.trim()}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-umber text-cream transition hover:bg-umber-dark disabled:opacity-40"
                >
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}

function MessageBubble({ msg }) {
  const isVisitor = msg.sender === "visitor";
  const isAI = msg.sender === "ai";
  const isHuman = msg.sender === "human";

  if (isVisitor) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-umber px-3.5 py-2 text-sm text-cream">
          {msg.text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2">
      <div
        className={`mt-1 flex h-7 w-7 items-center justify-center rounded-full text-cream ${
          isAI ? "bg-moss" : "bg-terracotta"
        }`}
      >
        {isAI ? <Bot className="h-3.5 w-3.5" /> : <UserIcon className="h-3.5 w-3.5" />}
      </div>
      <div className="max-w-[80%]">
        <div className="mb-0.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-umber/60">
          {isAI ? "SeedBot" : msg.sender_name || "SeedTechNG team"}
          {isHuman && <span className="rounded-full bg-terracotta/10 px-1.5 py-0.5 text-[9px] text-terracotta">human</span>}
        </div>
        <div className="rounded-2xl rounded-bl-sm border border-border bg-white px-3.5 py-2 text-sm text-umber whitespace-pre-wrap">
          {msg.text}
        </div>
      </div>
    </div>
  );
}
